import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { createHandleString } from './createHandleString';
import { safeParseURL } from './safeParseURL';
import { modifyUrlString } from './modifyUrlString';

export const ADD_PRODUCT_TO_CART_ERROR_MSG =
  'An error occurred while adding a product.';

export const INVENTORY_POLICIES = {
  CONTINUE: 'continue',
  DENY: 'deny',
};

export const updateAvailabilityAddToCartNavBtns = () => {
  const addToCartBtns = document.querySelectorAll('[data-add-to-cart]');
  const addToCartSubmitBtn = document.querySelector(
    '[data-add-to-cart-submit]'
  );

  addToCartBtns.forEach((btn) => {
    const submitBtnIsDisabled = Boolean(
      addToCartSubmitBtn.getAttribute('disabled')
    );

    if (submitBtnIsDisabled) {
      btn.setAttribute('disabled', 'true');
    } else {
      btn.removeAttribute('disabled');
    }
  });
};

export const changeActiveThumb = (index) => {
  const sliderThumbsEls = document.querySelectorAll(
    '[data-product-slider-thumb]'
  );

  sliderThumbsEls.forEach((thumb) => {
    const isActiveThumb =
      thumb.getAttribute('data-thumb-index') === String(index);

    if (isActiveThumb) {
      thumb.setAttribute('data-active', 'true');
    } else {
      thumb.setAttribute('data-active', 'false');
    }
  });
};

export const forceProductVideoStart = (index, isMobile) => {
  const autoPlayOnMobile = false;
  const autoplay = !isMobile || autoPlayOnMobile;

  const video = document.querySelector(`[data-slide-index="${index}"] video`);

  if (video) {
    const playPromise = video.play();
    const elementParent = video.closest(`[data-slide-index="${index}"]`);

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          video.setAttribute('data-video-started', 'true');
          elementParent.setAttribute('data-video-loaded', 'true');

          if (!autoplay || index > 0) {
            video.pause();
            video.setAttribute('data-video-started', 'false');
          }
        })
        .catch((error) => {
          elementParent.setAttribute('data-video-loaded', 'false');
          video.pause();

          // Ignore "NotAllowedError" if the user has disabled AutoPlay
          // video in the browser and aborted the function.
          if (error.name && error.name === 'NotAllowedError') {
            console.info(error.name);
          }
        });
    }
  }
};

export const videoToggle = (videoWrapperEl) => {
  const video = videoWrapperEl.querySelector('video');

  if (video.paused || video.currentTime === video.duration) {
    video.currentTime = 0;
    video.play();
    video.setAttribute('data-video-started', 'true');
  } else {
    video.pause();
    video.setAttribute('data-video-started', 'false');
  }
};

export const videoEnded = (videoEl) => {
  videoEl.setAttribute('data-video-started', 'false');
};

export const getVariantIdByUrl = (productConfig) => {
  const URL_PARAM_NAME = 'variant';

  if (
    !productConfig ||
    !isObject(productConfig) ||
    isEmpty(productConfig) ||
    !productConfig.variants ||
    Object.values(productConfig.variants) < 1
  ) {
    return null;
  }

  const variantIds = Object.values(productConfig?.variants || {}).map(
    ({ id }) => Number(id)
  );

  if (!variantIds.length) {
    return null;
  }

  const safeUrl = safeParseURL(window?.location);

  if (!safeUrl) {
    return variantIds[0];
  }

  const urlVariantParam = safeUrl.searchParams.get(URL_PARAM_NAME);

  if (!urlVariantParam) {
    return variantIds[0];
  }

  if (!variantIds.includes(Number(urlVariantParam))) {
    return variantIds[0];
  }

  return urlVariantParam;
};

const isCorrectPriceParam = (shopifyPrice) => {
  if (isString(shopifyPrice) || isNumber(shopifyPrice)) {
    return shopifyPrice > 0;
  }

  return false;
};

export const formatPrice = (shopifyPrice) => {
  if (isCorrectPriceParam(shopifyPrice)) {
    const shopifyPriceValue = (shopifyPrice / 100).toFixed(2);

    return `$${shopifyPriceValue}`;
  }

  return null;
};

export const formatPriceWithoutDecimals = (shopifyPrice) => {
  if (isCorrectPriceParam(shopifyPrice)) {
    const shopifyPriceValue = Math.floor(
      Number((shopifyPrice / 100).toFixed(2))
    );

    return `$${shopifyPriceValue}`;
  }

  return null;
};

export const formatPriceWithoutZeros = (shopifyPrice) => {
  if (isCorrectPriceParam(shopifyPrice)) {
    const isRoundPrice = Number.isInteger(shopifyPrice / 100);

    const shopifyPriceValue = (shopifyPrice / 100).toFixed(
      isRoundPrice ? 0 : 2
    );

    return `$${shopifyPriceValue}`;
  }

  return null;
};

export const formatPriceWithoutFractional = (shopifyPrice) => {
  if (isCorrectPriceParam(shopifyPrice)) {
    const shopifyPriceValue = Number((shopifyPrice / 100).toFixed(2));

    return Math.floor(shopifyPriceValue) >= 1
      ? `$${Math.floor(shopifyPriceValue)}`
      : `$${shopifyPriceValue}`;
  }

  return null;
};

export const calcSalePrice = (price, discountPrice = null) =>
  discountPrice || price;

export const calcNetPrice = (
  price,
  compareAtPrice = null,
  discountPrice = null
) => (discountPrice ? price : compareAtPrice || price);

export const calcDiscountAmount = (
  price,
  compareAtPrice = null,
  discountPrice = null
) => {
  if (discountPrice) {
    return price - discountPrice;
  }
  if (compareAtPrice) {
    return compareAtPrice - price;
  }
  return null;
};

const calcHandleizedOptions = (option1, option2, option3) => {
  const handleizedOption1 = createHandleString(option1) || null;
  const handleizedOption2 = createHandleString(option2) || null;
  const handleizedOption3 = createHandleString(option3) || null;

  const handleizedOptions = [
    handleizedOption1,
    handleizedOption2,
    handleizedOption3,
  ].filter(Boolean);

  return {
    handleizedOption1,
    handleizedOption2,
    handleizedOption3,
    handleizedOptions,
  };
};

const normalizeVariants = (config) =>
  config.variants.map((variant) => {
    const handleizedOptions = calcHandleizedOptions(
      variant.option1,
      variant.option2,
      variant.option3
    );

    return {
      ...variant,
      ...handleizedOptions,
    };
  });

export const normalizeProductConfig = (config) => ({
  ...config,
  variants: normalizeVariants(config),
});

export const addOffsetToStickyProductSlider = (stickyGallerySelector) => {
  const stickyGallery = document.querySelector(stickyGallerySelector);

  if (stickyGallery) {
    const headerNavigation = document.querySelector(
      '[data-theme-header-navigation]'
    );
    const headerNavigationTranslateY =
      headerNavigation.style.transform.match(/\d/g)?.join('') || 0;
    const headerNavigationHeight = headerNavigation
      ? headerNavigation.offsetHeight - headerNavigationTranslateY
      : 0;

    stickyGallery.style.top = `${headerNavigationHeight}px`;
  }
};

export const updateProductUrl = (variantId, url) => {
  const newUrl = modifyUrlString({
    baseUrl: window.location.href,
    path: url,
    search: {
      variant: variantId,
    },
  });

  window.history.replaceState({}, '', newUrl);
};

const { cartConfig } = window;

export const isBundleAvailable = (bundleConfig) =>
  bundleConfig.variants.every((bundleVariant, index) => {
    const { inventoryQuantity, inventoryPolicy } =
      cartConfig.variantsMainData[bundleVariant.id];
    const quantity = bundleConfig.variants_quantity[index];

    return (
      inventoryPolicy === INVENTORY_POLICIES.CONTINUE ||
      inventoryQuantity >= quantity
    );
  });

export const findUnavailableBundleVariant = (bundleConfig) =>
  bundleConfig.variants.find((bundleVariant, index) => {
    const { inventoryQuantity, inventoryPolicy } =
      cartConfig.variantsMainData[bundleVariant.id];
    const quantity = bundleConfig.variants_quantity[index];

    return (
      inventoryPolicy === INVENTORY_POLICIES.DENY &&
      inventoryQuantity < quantity
    );
  });

export const calcPreOrderMessage = (
  preOrderText,
  inventoryQuantity,
  inventoryPolicy,
  minQuantity = 1
) => {
  if (
    inventoryPolicy === INVENTORY_POLICIES.CONTINUE &&
    inventoryQuantity < minQuantity
  ) {
    return preOrderText || '';
  }

  return '';
};

export const calcBundlePreOrderMessage = (bundleConfig, preOrderText) => {
  let bundleInventoryMessage = '';

  bundleConfig.variants.some((bundleVariant, index) => {
    const { inventoryQuantity, inventoryPolicy } =
      cartConfig.variantsMainData[bundleVariant.id];
    const quantity = bundleConfig.variants_quantity[index];
    const variantInventoryMessage = calcPreOrderMessage(
      preOrderText,
      inventoryQuantity,
      inventoryPolicy,
      quantity
    );

    if (variantInventoryMessage) {
      bundleInventoryMessage = variantInventoryMessage;

      return true;
    }

    return false;
  });

  return bundleInventoryMessage;
};

export const calcBundleVariantsForSell = (bundleConfig, hasProperties = true) =>
  bundleConfig.variants.map(({ id }, index) => ({
    id,
    quantity: bundleConfig.variants_quantity[index] || 1,
    properties: hasProperties ? { Bundle: bundleConfig.property_value } : {},
  })) || [];
